/**
 * @features/property/hooks/useProperties.js
 * -----------------------------------------
 * Paginated search hook. Wraps `searchProperties` from the service and
 * exposes a flat React-friendly API designed for infinite-scroll lists.
 *
 * Usage:
 *   const { items, total, isLoading, isLoadingMore, hasMore, loadMore, error } =
 *     useProperties({ filters: { main_area, bhk }, pageSize: 24, sort: "created_at:desc" });
 *
 *   // Render `items` in a grid. Drop a sentinel `<div ref={sentinelRef} />`
 *   // at the bottom; this hook auto-loads the next page when it enters
 *   // the viewport (IntersectionObserver-based, lazy and rAF-friendly).
 *
 * Behaviour:
 *   • Resets to page 1 whenever `filters` or `sort` change (deep-compared via stringify).
 *   • Concurrent requests are aborted via AbortController so a fast filter
 *     change doesn't race an in-flight slow request to clobber state.
 *   • Tracks `isLoading` (fresh load) vs `isLoadingMore` (appending pages)
 *     separately for skeleton vs spinner UX.
 *
 * v2.71.26 — Initial extraction.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { searchProperties } from "../services/propertyService";
import { useDataSync } from "../../../lib/dataSync";

const DEFAULT_PAGE_SIZE = 24;
const DEFAULT_SORT = "created_at:desc";

/** Deep-stringify of a filter object for change detection. */
const stableKey = (obj) =>
  JSON.stringify(obj || {}, Object.keys(obj || {}).sort());

export function useProperties({
  filters = {},
  pageSize = DEFAULT_PAGE_SIZE,
  sort = DEFAULT_SORT,
  enabled = true,
  // v2.75 — Phase 2: When the page mounts via browser Back / Refresh and the
  // URL carries `?page=N`, the hook does ONE oversized fetch (page_size = N*24,
  // capped at the backend's 100-item ceiling) to reconstruct the user's prior
  // browsing depth. Default behaviour (fresh visit, no URL hint) is UNCHANGED:
  // single page of 24 items, append-mode infinite scroll.
  initialPage = 1,
} = {}) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(initialPage);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);       // initial / reset load
  const [isLoadingMore, setIsLoadingMore] = useState(false); // appending load
  const [error, setError] = useState(null);

  const filterKey = stableKey(filters);
  const abortRef = useRef(null);
  // Sentinel ref consumers attach to a bottom div for infinite-scroll.
  const sentinelRef = useRef(null);

  /** Fetch one page. `mode` is "fresh" (replace items) or "append". */
  const fetchPage = useCallback(
    async (pageNum, mode, sizeOverride) => {
      if (!enabled) return;
      if (abortRef.current) abortRef.current.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      try {
        if (mode === "fresh") setIsLoading(true);
        else if (mode === "append") setIsLoadingMore(true);
        // mode === "silent" → background refresh, no loading flags (no flash)
        setError(null);
        const r = await searchProperties({
          filters,
          page: pageNum,
          pageSize: sizeOverride || pageSize,
          sort,
          signal: ac.signal,
        });
        if (ac.signal.aborted) return;
        setTotal(r.total);
        setHasMore(r.hasNext);
        // v2.75 — During a restoration fetch (sizeOverride present) the
        // response says page=1 regardless of logical depth. Skip setPage
        // here; the items-derived effect below will surface the correct
        // logical page once items land in state. This prevents the URL
        // from briefly flickering `?page=2` → `/` → `?page=2`.
        if (!sizeOverride) setPage(r.page);
        setItems((prev) => (mode === "append" ? [...prev, ...r.items] : r.items));
      } catch (e) {
        if (e?.name === "CanceledError" || e?.name === "AbortError") return;
        setError(e);
      } finally {
        if (mode === "fresh") setIsLoading(false);
        else if (mode === "append") setIsLoadingMore(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterKey, pageSize, sort, enabled]
  );

  // Reset + first-page load whenever filters / sort change.
  // v2.75 — On first load with initialPage > 1, reconstruct the prior depth
  // in a SINGLE request (capped at 100). Subsequent filter changes always
  // reset to page 1, 24 items (normal infinite-scroll resumes).
  //
  // StrictMode-safe: refs persist across the dev-mode double-effect run on
  // the same instance. We track the pending restoration size in a ref so
  // the second effect re-run re-issues the same oversized fetch (otherwise
  // the AbortController would silently cancel restoration and fall back to
  // a normal 24-item fetch).
  const initialPageRef = useRef(initialPage);
  const restorationSizeRef = useRef(null);
  useEffect(() => {
    const ip = initialPageRef.current;
    const pending = restorationSizeRef.current;
    if (ip > 1 || pending) {
      const restoreSize = pending || Math.min(ip * pageSize, 100);
      restorationSizeRef.current = restoreSize; // remember for StrictMode re-run
      initialPageRef.current = 1; // consume so filter-changes use the normal path
      fetchPage(1, "fresh", restoreSize).finally(() => {
        // Cleared regardless of abort outcome; the StrictMode re-run will
        // have already set it again if still in flight.
        if (restorationSizeRef.current === restoreSize) {
          restorationSizeRef.current = null;
        }
      });
    } else {
      fetchPage(1, "fresh");
    }
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, sort, enabled]);

  // v2.75 — Surface the *logical* page count (= items.length / pageSize)
  // so the URL sync layer can persist `?page=N`. This decouples page state
  // from the literal backend response (which always says page=1 for the
  // restoration call) and stays correct under StrictMode's double-effect
  // run.
  useEffect(() => {
    if (items.length > pageSize) {
      const logicalPage = Math.ceil(items.length / pageSize);
      if (logicalPage !== page) setPage(logicalPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length, pageSize]);

  /** Imperatively load the next page (also auto-fired by the sentinel observer). */
  const loadMore = useCallback(() => {
    if (isLoading || isLoadingMore || !hasMore) return;
    fetchPage(page + 1, "append");
  }, [isLoading, isLoadingMore, hasMore, page, fetchPage]);

  // v2.72 — Live sync: when any property mutation is announced on the
  // data-sync bus the LRU search cache has already been invalidated; quietly
  // re-fetch page 1 so public lists update without a reload. Deep-scroll
  // sessions (page > 1) keep their position — they get fresh data on the
  // next filter change / navigation instead of a jarring list reset.
  const pageStateRef = useRef(1);
  pageStateRef.current = page;
  useDataSync(["properties"], () => {
    if (pageStateRef.current === 1) fetchPage(1, "silent");
  });

  // IntersectionObserver auto-load: when the sentinel enters the viewport
  // (with a 200px rootMargin so we start fetching just before the user
  // actually reaches the bottom), trigger loadMore. Cleaned up on unmount.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === "undefined") return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadMore();
      },
      { rootMargin: "200px 0px 200px 0px", threshold: 0 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [loadMore]);

  return {
    items,
    total,
    page,
    pageSize,
    hasMore,
    isLoading,
    isLoadingMore,
    error,
    loadMore,
    sentinelRef,
  };
}
